/**
 * CookieConsentBanner
 *
 * Marketing consent for the Meta Pixel. Looks like the Complianz banner of
 * novofon.ai (sizes and colors from its banner-1-optin.css), so visitors
 * coming from the website recognize it. It stays light in the dark theme,
 * like the website, and does not block the page.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '../../contexts/useConsent';
import { cn } from '../../utils/cn';

const COOKIE_POLICY_URL = 'https://novofon.ai/cookie-richtlinie-eu/';
const PRIVACY_URL = 'https://novofon.ai/datenschutz/';
const IMPRINT_URL = 'https://novofon.ai/impressum/';

type Category = 'functional' | 'marketing';

const BUTTON =
  'min-h-[45px] w-full cursor-pointer whitespace-nowrap rounded-[6px] border p-[10px] text-[15px] font-medium leading-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#245fcc]';
const BUTTON_ACCEPT = 'border-[#1E73BE] bg-[#1E73BE] text-white';
const BUTTON_SECONDARY = 'border-[#f2f2f2] bg-[#f9f9f9]';

const CloseIcon: React.FC = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 352 512" width="20" height="20">
    <path
      fill="currentColor"
      d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
    />
  </svg>
);

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 448 512"
    height="18"
    width="18"
    className={cn('transition-transform duration-500', open && 'rotate-180')}
  >
    <path
      fill="currentColor"
      d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z"
    />
  </svg>
);

export const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const { bannerVisible, settingsOpen, marketing, accept, decline, saveSettings, dismiss } =
    useConsent();
  const [showSettings, setShowSettings] = useState(settingsOpen);
  const [marketingChoice, setMarketingChoice] = useState(marketing);
  const [expanded, setExpanded] = useState<Category | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bannerVisible) return;
    setShowSettings(settingsOpen);
    setMarketingChoice(marketing);
    setExpanded(null);
    if (settingsOpen) dialogRef.current?.focus();
  }, [bannerVisible, settingsOpen, marketing]);

  if (!bannerVisible) return null;

  const toggle = (category: Category) =>
    setExpanded(current => (current === category ? null : category));

  const renderCategory = (category: Category, control: React.ReactNode) => (
    <div className="bg-[rgba(239,239,239,0.5)]">
      <div className="grid grid-cols-[1fr_auto_15px] items-center gap-[10px] p-[10px] max-[425px]:grid-cols-[1fr]">
        <span id={`cookie-consent-${category}-title`} className="text-[14px] font-medium">
          {t(`cookieConsent.categories.${category}.title`)}
        </span>
        {control}
        <button
          type="button"
          className="col-start-3 flex cursor-pointer items-center justify-center max-[425px]:col-start-1"
          aria-expanded={expanded === category}
          aria-controls={`cookie-consent-${category}-description`}
          aria-labelledby={`cookie-consent-${category}-title`}
          onClick={() => toggle(category)}
        >
          <ChevronIcon open={expanded === category} />
        </button>
      </div>
      <div
        id={`cookie-consent-${category}-description`}
        hidden={expanded !== category}
        className="p-[10px] text-[12px]"
      >
        {t(`cookieConsent.categories.${category}.description`)}
      </div>
    </div>
  );

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-message"
      tabIndex={-1}
      className={cn(
        'fixed bottom-0 left-0 z-[90] grid max-h-[calc(100vh-20px)] w-full gap-[10px] overflow-y-auto',
        'rounded-[12px] bg-white px-5 py-[15px] text-[#222222] focus:outline-none',
        'shadow-[0_10px_20px_rgba(0,0,0,0.19),0_6px_6px_rgba(0,0,0,0.23)]',
        'md:bottom-[10px] md:left-[10px] md:w-[526px] md:max-w-[calc(100vw-20px)]'
      )}
    >
      <div className="grid grid-cols-[100px_1fr_100px] items-center max-[399px]:grid-cols-[auto_1fr_auto]">
        <h2
          id="cookie-consent-title"
          className="col-start-2 justify-self-center text-[15px] font-medium max-[425px]:hidden"
        >
          {t('cookieConsent.title')}
        </h2>
        <button
          type="button"
          className="col-start-3 h-5 w-5 cursor-pointer justify-self-end"
          aria-label={t('cookieConsent.close')}
          onClick={dismiss}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="max-h-[55vh] overflow-y-auto overflow-x-hidden">
        <p id="cookie-consent-message" className="mb-[5px] text-[12px] leading-[18px]">
          {t('cookieConsent.message')}
        </p>
        {showSettings && (
          <div className="space-y-[10px]">
            {renderCategory(
              'functional',
              <span className="text-[12px] font-medium text-[green]">
                {t('cookieConsent.alwaysActive')}
              </span>
            )}
            {renderCategory(
              'marketing',
              <button
                type="button"
                role="switch"
                aria-checked={marketingChoice}
                aria-labelledby="cookie-consent-marketing-title"
                onClick={() => setMarketingChoice(value => !value)}
                className={cn(
                  'relative h-[15px] w-[28px] cursor-pointer rounded-[10px] transition-colors duration-300',
                  'focus-visible:shadow-[0_0_0_2px_#245fcc] focus-visible:outline-none',
                  marketingChoice ? 'bg-[#1e73be]' : 'bg-[#F56E28]'
                )}
              >
                <span
                  className={cn(
                    'absolute top-[2px] h-[11px] w-[11px] rounded-[10px] bg-white transition-[left] duration-300',
                    marketingChoice ? 'left-[14px]' : 'left-[4px]'
                  )}
                />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-[10px] md:flex-row">
        <button type="button" className={cn(BUTTON, BUTTON_ACCEPT)} onClick={accept}>
          {t('cookieConsent.accept')}
        </button>
        <button
          type="button"
          className={cn(BUTTON, BUTTON_SECONDARY, 'text-[#222222]')}
          onClick={decline}
        >
          {t('cookieConsent.decline')}
        </button>
        {showSettings ? (
          <button
            type="button"
            className={cn(BUTTON, BUTTON_SECONDARY, 'text-[#333333]')}
            onClick={() => saveSettings(marketingChoice)}
          >
            {t('cookieConsent.savePreferences')}
          </button>
        ) : (
          <button
            type="button"
            className={cn(BUTTON, BUTTON_SECONDARY, 'text-[#333333]')}
            onClick={() => setShowSettings(true)}
          >
            {t('cookieConsent.viewPreferences')}
          </button>
        )}
      </div>

      <ul className="m-0 flex list-none justify-center gap-[10px] p-0">
        {[
          { href: COOKIE_POLICY_URL, label: t('cookieConsent.cookiePolicy') },
          { href: PRIVACY_URL, label: t('cookieConsent.privacy') },
          { href: IMPRINT_URL, label: t('cookieConsent.imprint') },
        ].map(link => (
          <li key={link.href}>
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] text-[#1E73BE] underline"
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};
